'use client';
import includes from 'lodash.includes';
import React, { useMemo, Fragment, useCallback } from 'react';
import { useSyncExternalStoreShim, useBodyScrollLock } from '@financial/common-hooks';
import BottomModal from '../components/modal/bottomModal/BottomModal.mjs';
import { BottomModalNoticeAd, BottomModalNoticeFull, BottomModalNoticeImage, BottomModalNoticeText } from '../components/modal/bottomModalNotice/BottomModalNotice.mjs';
import CenterModalConfirm from '../components/modal/centerModal/CenterModalConfirm.mjs';
import CenterModalNotice from '../components/modal/centerModal/CenterModalNotice.mjs';
import TopModal from '../components/modal/topModal/TopModal.mjs';
import { RESPONSIVE } from '../constants/global.mjs';
import { MODAL_CATEGORY, BOTTOM_TOP_MODAL_TYPE, NONE_MODAL_TYPE, BOTTOM_MODAL_NOTICE_TYPE, CENTER_MODAL_TYPE } from '../constants/modal.mjs';
import { getRandomString } from '../utils/string.mjs';

const isBottomTopModal = category => includes([MODAL_CATEGORY.BOTTOM, MODAL_CATEGORY.TOP], category);
let listeners = [];
let modalStateInternal = {
  modals: [],
  preRenderModalMap: {}
};
const updateModal = next => {
  if (typeof next === 'function') {
    modalStateInternal = next(modalStateInternal);
  } else {
    modalStateInternal = {
      ...modalStateInternal,
      ...next
    };
  }
  listeners.forEach(listener => listener());
};
const modalStateSyncStore = {
  getSnapshot() {
    return modalStateInternal;
  },
  subscribe(listener) {
    listeners = [...listeners, listener];
    return () => {
      listeners = listeners.filter(l => l !== listener);
    };
  }
};
const modalInternal = {
  create: function (category, params) {
    let isPreRender = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    const _modal = {
      key: getRandomString(category),
      category: category,
      isOpen: !isPreRender,
      isShow: false,
      params: {
        ...params,
        ...(category === MODAL_CATEGORY.BOTTOM && {
          type: BOTTOM_TOP_MODAL_TYPE.BOTTOM
        }),
        ...(category === MODAL_CATEGORY.TOP && {
          type: BOTTOM_TOP_MODAL_TYPE.TOP
        })
      }
    };
    if (isPreRender) {
      updateModal({
        preRenderModalMap: {
          ...modalStateInternal.preRenderModalMap,
          [category]: _modal
        }
      });
      return;
    }
    updateModal({
      modals: modalStateInternal.modals.filter(prevModal => prevModal.category !== category).concat(_modal)
    });
  },
  show: category => {
    const preRenderModal = modalStateInternal.preRenderModalMap[category];
    if (preRenderModal) {
      const nextPreRenderModalMap = {
        ...modalStateInternal.preRenderModalMap
      };
      delete nextPreRenderModalMap[category];
      updateModal({
        modals: modalStateInternal.modals.filter(prevModal => prevModal.category !== category).concat({
          ...preRenderModal,
          isShow: true
        }),
        preRenderModalMap: nextPreRenderModalMap
      });
    }
    requestAnimationFrame(() => {
      updateModal({
        modals: modalStateInternal.modals.map(prevModal => prevModal.category === category ? {
          ...prevModal,
          isShow: true
        } : prevModal)
      });
    });
  },
  hide: function (category) {
    let toBeDestroyed = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    updateModal({
      modals: modalStateInternal.modals.map(prevModal => prevModal.category === category ? {
        ...prevModal,
        isShow: false,
        toBeDestroyed
      } : prevModal)
    });
  },
  destroy: category => {
    const nextPreRenderModalMap = {
      ...modalStateInternal.preRenderModalMap
    };
    delete nextPreRenderModalMap[category];
    updateModal({
      preRenderModalMap: nextPreRenderModalMap,
      modals: modalStateInternal.modals.filter(prevModal => prevModal.category !== category)
    });
  },
  clear: () => {
    updateModal({
      modals: [],
      preRenderModalMap: {}
    });
  }
};
const useModalStore = () => useSyncExternalStoreShim(modalStateSyncStore.subscribe, modalStateSyncStore.getSnapshot);
const renderBottomModalNotice = _ref => {
  let {
    key,
    params,
    isPreRender
  } = _ref;
  const {
    type
  } = params;
  switch (type) {
    case BOTTOM_MODAL_NOTICE_TYPE.TEXT:
      {
        const {
          title,
          subText,
          button,
          underlineButton,
          onClickDimmedLayer,
          onClickCloseButton,
          hasBottomNavigationBar
        } = params;
        return /*#__PURE__*/React.createElement(BottomModalNoticeText, {
          key: key,
          isPreRender: isPreRender,
          hasBottomNavigationBar: hasBottomNavigationBar,
          title: title,
          subText: subText,
          button: button,
          underlineButton: underlineButton,
          onClickDimmedLayer: onClickDimmedLayer,
          onClickCloseButton: onClickCloseButton
        });
      }
    case BOTTOM_MODAL_NOTICE_TYPE.IMAGE:
      {
        const {
          title,
          subText,
          image,
          imagePosition,
          button,
          underlineButton,
          onClickDimmedLayer,
          onClickCloseButton,
          hasBottomNavigationBar
        } = params;
        return /*#__PURE__*/React.createElement(BottomModalNoticeImage, {
          key: key,
          isPreRender: isPreRender,
          hasBottomNavigationBar: hasBottomNavigationBar,
          title: title,
          subText: subText,
          image: image,
          imagePosition: imagePosition,
          button: button,
          underlineButton: underlineButton,
          onClickDimmedLayer: onClickDimmedLayer,
          onClickCloseButton: onClickCloseButton
        });
      }
    case BOTTOM_MODAL_NOTICE_TYPE.FULL:
      {
        const {
          image,
          button,
          underlineButton,
          onClickDimmedLayer,
          onClickCloseButton,
          hasBottomNavigationBar
        } = params;
        return /*#__PURE__*/React.createElement(BottomModalNoticeFull, {
          key: key,
          isPreRender: isPreRender,
          hasBottomNavigationBar: hasBottomNavigationBar,
          image: image,
          button: button,
          underlineButton: underlineButton,
          onClickDimmedLayer: onClickDimmedLayer,
          onClickCloseButton: onClickCloseButton
        });
      }
    case BOTTOM_MODAL_NOTICE_TYPE.AD:
      {
        const {
          content,
          underlineButton,
          onClickDimmedLayer,
          onClickCloseButton,
          hasBottomNavigationBar
        } = params;
        return /*#__PURE__*/React.createElement(BottomModalNoticeAd, {
          key: key,
          isPreRender: isPreRender,
          hasBottomNavigationBar: hasBottomNavigationBar,
          content: content,
          underlineButton: underlineButton,
          onClickDimmedLayer: onClickDimmedLayer,
          onClickCloseButton: onClickCloseButton
        });
      }
    default:
      return null;
  }
};
const renderCenterModal = _ref2 => {
  let {
    key,
    params
  } = _ref2;
  const {
    type
  } = params;
  switch (type) {
    case CENTER_MODAL_TYPE.NOTICE:
      {
        const {
          button,
          content,
          onClickDimmedLayer
        } = params;
        return /*#__PURE__*/React.createElement(CenterModalNotice, {
          key: key,
          button: button,
          content: content,
          onClickDimmedLayer: onClickDimmedLayer
        });
      }
    case CENTER_MODAL_TYPE.CONFIRM:
      {
        const {
          buttons,
          content,
          onClickDimmedLayer
        } = params;
        return /*#__PURE__*/React.createElement(CenterModalConfirm, {
          key: key,
          buttons: buttons,
          content: content,
          onClickDimmedLayer: onClickDimmedLayer
        });
      }
    default:
      return null;
  }
};
const renderBottomModal = _ref3 => {
  let {
    key,
    params,
    isPreRender
  } = _ref3;
  const {
    onClickCloseButton,
    onClickDimmedLayer,
    children,
    isShowCloseButton,
    closeButtonText,
    role,
    customClassName
  } = params;
  if (!params.children) {
    return null;
  }
  return /*#__PURE__*/React.createElement(BottomModal, {
    key: key,
    isPreRender: isPreRender,
    isShowCloseButton: isShowCloseButton,
    closeButtonText: closeButtonText,
    onClickCloseButton: onClickCloseButton,
    onClickDimmedLayer: onClickDimmedLayer,
    customClassName: customClassName,
    role: role
  }, children);
};
const renderTopModal = _ref4 => {
  let {
    key,
    params,
    isPreRender
  } = _ref4;
  const {
    onClickCloseButton,
    onClickDimmedLayer,
    children,
    isShowCloseButton,
    closeButtonText,
    role,
    customClassName
  } = params;
  if (!params.children) {
    return null;
  }
  return /*#__PURE__*/React.createElement(TopModal, {
    key: key,
    isPreRender: isPreRender,
    isShowCloseButton: isShowCloseButton,
    closeButtonText: closeButtonText,
    onClickCloseButton: onClickCloseButton,
    onClickDimmedLayer: onClickDimmedLayer,
    customClassName: customClassName,
    role: role
  }, children);
};
function getModalComponent(_ref5) {
  let {
    key,
    category,
    params
  } = _ref5;
  let isPreRender = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  switch (category) {
    case MODAL_CATEGORY.BOTTOM_NOTICE:
      return renderBottomModalNotice({
        key,
        params: params,
        isPreRender
      });
    case MODAL_CATEGORY.CENTER:
      return renderCenterModal({
        key,
        params: params
      });
    case MODAL_CATEGORY.BOTTOM:
      return renderBottomModal({
        key,
        params: params,
        isPreRender
      });
    case MODAL_CATEGORY.TOP:
      return renderTopModal({
        key,
        params: params,
        isPreRender
      });
    default:
      return null;
  }
}
function ModalProvider(_ref6) {
  let {
    children
  } = _ref6;
  const {
    modals,
    preRenderModalMap
  } = useModalStore();
  const isSomeModalRunning = modals.some(m => m.isShow);
  useBodyScrollLock(isSomeModalRunning);
  const preRenderModals = useMemo(() => Object.values(preRenderModalMap), [preRenderModalMap]);
  return /*#__PURE__*/React.createElement(Fragment, null, children, modals.map(m => getModalComponent(m, false)), preRenderModals.map(m => getModalComponent(m, true)));
}
/**
 * 외부 노출용 모달 제어 인터페이스
 */
const modal = {
  /**
   * 모달을 생성하고 엽니다.
   * @param category 모달 카테고리
   * @param params 모달 카테고리에 따른 파라미터
   */
  open: (category, params) => {
    modalInternal.create(category, params, false);
    modalInternal.show(category);
  },
  /**
   * 모달 내의 라이프사이클을 사용하기 위하여 모달을 미리 랜더링합니다.
   * 이때 랜더 함수의 리턴값은 null입니다. 실제로 화면에 그려지는 것은 없습니다.
   * 나중에 모달 노출이 필요할 때 preRenderOpen을 사용합니다.
   * @param category  모달 카테고리
   * @param params  모달 카테고리에 따른 파라미터
   */
  preRender: (category, params) => {
    modalInternal.create(category, params, true);
  },
  /**
   * prerender로 생성된 모달을 화면에 노출합니다.
   * @param category 모달 카테고리
   */
  preRenderOpen: category => {
    modalInternal.show(category);
  },
  /**
   * 전달받은 카테고리의 모달을 닫습니다.
   * @param category 모달 카테고리
   */
  close: category => {
    modalInternal.hide(category);
    const mqDesktop = window.matchMedia(`(min-width: ${RESPONSIVE.DESKTOP}px)`);
    const hasNoTransitionAnimation = isBottomTopModal(category) && mqDesktop.matches;
    if (hasNoTransitionAnimation) {
      modalInternal.destroy(category);
    }
  },
  /**
   * 생성된 모든 모달을 제거합니다.
   * 모달의 라이프사이클을 사용하지 않고 전체를 제거할 때 사용합니다.
   * 페이지 이동시 잔여모달을 애니메이션 없이 제거할때 사용할 수 있습니다.
   */
  clear: modalInternal.clear
};
function useModal(category) {
  var _target$params;
  const {
    modals
  } = useModalStore();
  const target = modals.find(m => m.category === category);
  const open = useCallback(params => {
    modal.open(category, params);
  }, [category]);
  const preRender = useCallback(params => modal.preRender(category, params), [category]);
  const preRenderOpen = useCallback(() => modal.preRenderOpen(category), [category]);
  const close = useCallback(() => {
    modal.close(category);
  }, [category]);
  return {
    isOpen: (target === null || target === void 0 ? void 0 : target.isOpen) ?? false,
    type: (target === null || target === void 0 || (_target$params = target.params) === null || _target$params === void 0 ? void 0 : _target$params.type) ?? NONE_MODAL_TYPE,
    open,
    preRender,
    preRenderOpen,
    close
  };
}
function useBottomModalNotice() {
  return useModal(MODAL_CATEGORY.BOTTOM_NOTICE);
}
function useCenterModal() {
  return useModal(MODAL_CATEGORY.CENTER);
}
function useBottomModal() {
  return useModal(MODAL_CATEGORY.BOTTOM);
}
function useTopModal() {
  return useModal(MODAL_CATEGORY.TOP);
}

export { ModalProvider, modal, modalInternal, useBottomModal, useBottomModalNotice, useCenterModal, useModalStore, useTopModal };
